import * as React from 'react';
import { LibraryBooks, MonetizationOnOutlined } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import useTablePage from '../hooks/useTablePage';
import {
  updatePermitActivity,
  getPermitActivities,
  getCustomActivities,
  createPermitActivityComment,
} from '../api/activities';
import tableColumnsFormatter from '../utils/tableColumnsFormatter';
import Table from './Table';
import ViewActivityFees from './Fees/ViewActivityFees';
import { convertTableBoolean } from '../utils/convertTableValues';
import ModalDefaultDraggable from '../molecules/ModalDefaultDraggable';
import PermitActivitiesForm from '../forms/PermitActivitiesForm';
import ModalInfo from '../molecules/ModalInfo';
import ContactNotAvailableModal from '../molecules/ContactNotAvailableModal';
import {
  DATETIME_FORMAT,
} from '../constants';
import { formatDateTime } from '../utils/formatDate';
import openInNewTab from '../utils/openInNewTab';
import { getVocabulariesDataList } from '../api/vocabulary';
import ModalFullScreen from '../molecules/ModalFullScreen';
import DocumentsDetailedPage from '../pages/Permits/DocumentsDetailedPage';
import { getReviewerComments } from '../api/projectDocuments';
import ReviewCommentLetter from '../pages/Reports/ReviewCommentLetter';
import { getContactString } from '../utils/setInspectionData';
import { RootState } from '../redux/root';

const PermitActionsTable = ({
  projectId,
  permit,
  activityType,
  activityCreated,
  setActivityCreated,
  cameFromSearch,
  setDisableReviewCommentBtn,
  showReviewCommentLtr,
  setShowReviewCommentLtr,
}: Props) => {
  const {
    tableManuallyUpdate,
    updateTable,
    search,
    handleEdit,
    isModalOpen,
    createOrUpdate,
    modalSuccess,
    modalError,
    modalTitle,
    modalErrorTitle,
    errors,
    values,
    handleCloseModalInfo,
    handleCloseModal,
  } = useTablePage({
    updateEndpoint: updatePermitActivity,
    createEndpoint: createPermitActivityComment,
    modalCreateTitle: 'Add Comment',
  });

  const [currentActivity, setCurrentActivity] = React.useState({});
  const [isViewFeesModalOpen, setIsViewFeesModalOpen] = React.useState(false);
  React.useEffect(() => {
    // A comment add/update or activity-type switch can change which reviews have
    // comments, so clear the guard to force a fresh check on the next fetch.
    checkedReviewIdsRef.current = '';
    updateTable();
  }, [
    activityType,
    modalSuccess,
    modalError,
    activityCreated,
  ]);

  // A successful activity add/update from this table can clear the permit's last
  // outstanding item, which unlocks the Completed / Withdrawn statuses. Tell the
  // permit form to re-run its gate check. SubPermitMenu's copy of this same modal
  // already signalled this, but the Activities-tab row "Update" did not, so
  // completing the final activity there left the dropdown locked until a full
  // page refresh. (EA-931 follow-up)
  React.useEffect(() => {
    if (modalSuccess) {
      setActivityCreated(true);
    }
  }, [modalSuccess]);

  const [documents, showDocuments] = React.useState(null);
  const closeInfoModal = () => {
    showDocuments(false);
    updateTable();
    handleCloseModalInfo();
  };
const [activityTypes, setActivityTypes] = React.useState([]);
const [reviews, setReviews] = React.useState(null);
const [customReviewsData, setCustomReviewsData] = React.useState(null);
// Remembers the review-id set we last checked for comments, so the shared Table
// re-firing its fetch (during init, sort, etc.) doesn't re-request the same
// get_review_comments calls over and over.
const checkedReviewIdsRef = React.useRef<string>('');
const hideCompleteActivities = useSelector((s:RootState) => s.projectsSlice.hideCompleteActivities);
useEffect(() => {
  updateTable();
}, [hideCompleteActivities]);

React.useEffect(() => {
  Promise.all([
    getCustomActivities({
      is_deleted: false,
      permit_id: permit,
      event_type: "Activity",
      activity_type_name: "Reviews",
    }),
     getVocabulariesDataList({
        // ...GET_ALL_QUERY_PARAMS,
        vocabularyEntity: 3,
        ordering: 'title',
      }),
  ]).then(([reviewsResponse, activityTypesResponse]) => {
    setReviews(reviewsResponse?.data?.results);
    const customReviews = reviewsResponse?.data?.results?.filter(el => !el?.reviewTypeComment?.length).map(item => item?.id);
    setCustomReviewsData(customReviews);
    setActivityTypes(activityTypesResponse?.data?.results);
  });
}, []);

  const modifiedGetPermitActivities = params => {
    const activityTypeTitle = activityTypes?.find(type => type?.id === activityType)?.title;
   return Promise.all([
      getCustomActivities({
        ...params,
        permit_id: permit,
        // activityType,
        sort_by: params?.ordering,
        ordering: null,
        event_type: "Activity",
        activity_type_name: activityTypeTitle === "Fee" ? "Fee_Activity" : activityTypeTitle,
        without_complete_records: hideCompleteActivities % 2 === 0 ? undefined : 'true',
      }),
    ])
      .then(
        ([
          activitiesResponse,
        ]) => {
          const reviewActivities = activitiesResponse?.data?.results?.filter(el => el?.activityTypeName === "Reviews");
          setReviews(reviewActivities);
          if (reviewActivities?.length) {
            const reviewIdsKey = reviewActivities.map(el => el?.id).sort().join(',');
            // Skip if we already checked comments for this exact set of reviews —
            // avoids the duplicate get_review_comments storm when the Table refires.
            if (reviewIdsKey !== checkedReviewIdsRef.current) {
              checkedReviewIdsRef.current = reviewIdsKey;
              const reviewCommentPromises = [];
              for (let i = 0; i < reviewActivities.length; i++) {
                reviewCommentPromises.push(getReviewerComments(reviewActivities[i]?.id, true));
              }
              Promise.all(reviewCommentPromises).then(responseList => {
                const arr = responseList?.map(el => el?.data)?.filter(el => el?.length > 0);
                if (arr.length) {
                  setDisableReviewCommentBtn(false);
                } else {
                  setDisableReviewCommentBtn(true);
                }
              });
            }
          } else {
            checkedReviewIdsRef.current = '';
            setDisableReviewCommentBtn(true);
          }
          return {
            ...activitiesResponse,
            data: {
              ...activitiesResponse.data,
              results: activitiesResponse.data.results.map(
                el => {
                  return {
                    ...el,
                    activityType: el?.activityTypeName,
                    title: el?.activityTitleName,
                    status: el?.statusName,
                    contact: getContactString(el),
                    assignedToId: el?.assignedTo,
                    assignedTo: `${el?.assignedToFirstName} ${el?.assignedToLastName}`,
                    followUp: formatDateTime(
                      el.followUp,
                      DATETIME_FORMAT,
                    ),
                    attachments:
                      el.attachments &&
                      el.attachments?.map(
                        file => file.visibleFilename,
                      ),
                    documentFile:
                      el.attachments &&
                      el.attachments?.map(doc => {
                        return doc?.file;
                      }),
                      isDeleted: cameFromSearch,
                  };
                },
              ),
            },
          };
        },
      )
      .catch(err => console.log(err))
      .finally(() => {});
};

  const columns = column => {
    if (activityType === 7) {
      if (
        [
          'permit',
          'amount',
          'id',
          'inspector',
          'isEmergency',
          'isCompleted',
          'information',
          'lastComment',
          'reviewTypeName',
          'fees',
          'feeNames',
          'documentFile',
          'isDeleted',
          'permitName',
          'permitNumber',
          'permitId',
          'contactFirstName',
          'contactLastName',
          'assignedToFirstName',
          'assignedToLastName',
          'companyName',
          'activityTitleName',
          'activityTypeName',
          'projectName',
          'projectJurisdiction',
          'statusName',
          'contactId',
          'type',
          'attachments',
          'projectId',
          'createdAt',
          'createdBy',
          'completedAt',
          'completedBy',
          'selectedDocuments',
          'inspectorFirstName',
          'inspectorLastName',
          'franchisePhoneNumber',
          'inspectorEmail',
          'projectAddress',
          'franchiseName',
          'reviewTypeComment',
          'fullInvoiceNumber',
          'file',
          'reviewTypeId',
          'contactEmail',
          'contactIsActive',
          'assignedToId',
        ].includes(column)
      ) {
        return null;
      }
    } else if (
        [
          'permit',
          'amount',
          'id',
          'inspector',
          'isEmergency',
          'isCompleted',
          'reviewType',
          'information',
          'lastComment',
          'reviewTypeName',
          'fees',
          'feeNames',
          'documentFile',
          'isDeleted',
          'permitName',
          'permitNumber',
          'permitId',
          'contactFirstName',
          'contactLastName',
          'assignedToFirstName',
          'assignedToLastName',
          'companyName',
          'activityTitleName',
          'activityTypeName',
          'projectName',
          'projectJurisdiction',
          'statusName',
          'contactId',
          'type',
          'attachments',
          'projectId',
          'createdAt',
          'createdBy',
          'completedAt',
          'completedBy',
          'selectedDocuments',
          'inspectorFirstName',
          'inspectorLastName',
          'franchisePhoneNumber',
          'inspectorEmail',
          'projectAddress',
          'franchiseName',
          'reviewTypeComment',
          'fullInvoiceNumber',
          'file',
          'reviewTypeId',
          'contactEmail',
          'contactIsActive',
          'assignedToId',
        ].includes(column)
      ) {
        return null;
      }

    switch (column) {
      case 'activityType':
        return 'Activity Type';
      case 'title':
        return 'Activity Title';
      case 'status':
        return 'Activity Status';
      case 'contact':
        return 'Contact';
      case 'reviewType':
        return 'Trade';
      case 'assignedTo':
        return 'Assigned to';
      case 'followUp':
        return 'Follow Up';
      case 'createdAt':
        return 'Date Created';
      case 'completedAt':
        return 'Date Completed';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const columnsOrder = [
    'activityType',
    'title',
    'status',
    'contact',
    'followUp',
    'assignedTo',
    'createdAt',
    'createdBy',
    'completedAt',
    'completedBy',
  ];

  const handleCloseActivitiesModal = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  const handleCellClick = rowValue => {
    if (
      rowValue.documentFile &&
      Array.isArray(rowValue.documentFile)
    ) {
      const link = rowValue.documentFile[0];
      openInNewTab(link);
    } else {
      openInNewTab(rowValue.documentFile);
    }
  };

  const cellCallbackFnIsOn = ['attachments'];

  const handleCloseViewFeesModal = () => {
    setCurrentActivity({});
    setIsViewFeesModalOpen(false);
  };
  const viewFeesActivity = row => {
    setIsViewFeesModalOpen(true);
      setCurrentActivity(row);
  };

  const additionalActions = row => {
    if ((row?.activityType === "Fee" && row?.title === "Invoice") || row?.fees?.length > 0) {
      return [
        {
          title: 'View Fees',
          handler: () => viewFeesActivity(row),
          Icon: MonetizationOnOutlined,
        },
      ];
    }
    if (row?.activityType === "Reviews") {
      return [
        {
          title: 'View Documents',
          handler: () => showDocuments(row),
          Icon: LibraryBooks,
        },
      ];
    }
    return undefined;
  };

  return (
    <>
      {reviews ? (customReviewsData ? <Table
        customSearch
        initialUseSortBy
        permitActivityTable
        searchQuery={search}
        columnsOrder={columnsOrder}
        colorSchema
        columns={columns}
        rows={modifiedGetPermitActivities}
        onEdit={handleEdit}
        onDelete={null}
        onConfigure={null}
        // initialSortBy={[{ id: 'createdAt', desc: true }]}
        additionalActions={additionalActions}
        handleCellClick={handleCellClick}
        cellCallbackFnIsOn={cellCallbackFnIsOn}
        isMultipleDocuments
        formatValue={convertTableBoolean}
        manuallyUpdate={tableManuallyUpdate}
        isPermitActivitiesPage
        italic
        reviewerCommentData={customReviewsData}
      /> : null) : <Table
        customSearch
        initialUseSortBy
        permitActivityTable
        searchQuery={search}
        columnsOrder={columnsOrder}
        colorSchema
        columns={columns}
        rows={modifiedGetPermitActivities}
        onEdit={handleEdit}
        onDelete={null}
        onConfigure={null}
      // initialSortBy={[{ id: 'createdAt', desc: true }]}
        additionalActions={additionalActions}
        handleCellClick={handleCellClick}
        cellCallbackFnIsOn={cellCallbackFnIsOn}
        isMultipleDocuments
        formatValue={convertTableBoolean}
        manuallyUpdate={tableManuallyUpdate}
        isPermitActivitiesPage
        italic
      />}
      {isViewFeesModalOpen && (
        <ModalDefaultDraggable
          extraWide
          title="View Fees"
          onClose={handleCloseViewFeesModal}
        >
          <ViewActivityFees activityId={currentActivity?.id} />
        </ModalDefaultDraggable>
      )}
      {isModalOpen && (
        <ModalDefaultDraggable
          title={modalTitle}
          onClose={handleCloseActivitiesModal}
          wide
        >
          <PermitActivitiesForm
            onSubmit={createOrUpdate}
            initialValues={values}
            modalError={modalError}
            addButtonText="Create"
            updateButtonText="Update"
            initialErrors={errors}
            permit={permit}
            setActivityCreated={setActivityCreated}
            cameFromSearch={cameFromSearch}
          />
        </ModalDefaultDraggable>
      )}
      {isModalOpen && (
        <ContactNotAvailableModal key={values?.id} record={values} />
      )}
      {/* Reviews comment data EA-551 */}
      {documents && (
        <ModalFullScreen
          title=""
          onClose={closeInfoModal}
        >
          <DocumentsDetailedPage
            permit={documents.permitId}
            work={null}
            building={null}
            isAddDocument={false}
            setAddDocument={null}
            isDownloadAllDocs={false}
            setDocsAvailable={null}
            setDownloadAllDocs={null}
            setDocsWaitingOnClient={null}
            isSendDocs={false}
            setSendDocs={null}
            isInspector
            initialValues={documents}
            onSubmit={function (data: any): void {
                throw new Error('Function not implemented.');
              }}
          />
        </ModalFullScreen>
      )}

      {showReviewCommentLtr && <ModalDefaultDraggable
        title=""
        onClose={() => setShowReviewCommentLtr(false)}
        disabledDraggable
        onClickAwayClose={false}
      >
        <ReviewCommentLetter reviews={reviews} setShowReviewCommentLtr={setShowReviewCommentLtr} />
      </ModalDefaultDraggable>}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={closeInfoModal}
          errorMsg={modalErrorTitle}
        />
      )}
    </>
  );
};

interface Props {
  permit: number;
  activityType: number;
  activityCreated: boolean;
  setActivityCreated: (data: boolean) => void;
}

export default PermitActionsTable;
